import { useEffect, useState } from 'react'

import { useT } from '../../i18n'
import { dexieLotCostEventRepo } from '../../offline/repos/DexieLotCostEventRepo'
import { isFacilityCost } from '../../utils/lotCostEventApi'
import type { ReportScopeV1 } from '../../utils/reportScope'
import { formatCostEventDisplayAmount, sumCostEventsByCurrency } from '../lots/lotCostForm'

// Facility-wide costs (findings #100) next to Reportes → "Costo por kg": the sum
// of the costs with no lot in the report's facility and dates. Never allocated,
// so no lot's cost per kg changes.

/** Pure filter, exported for tests. */
export const facilityCostsInScope = <E extends { lotId?: string; farmId: string; recordedAt: string; metadata?: Record<string, unknown> }>(
  events: E[],
  scope: Pick<ReportScopeV1, 'farmIds' | 'fromDate' | 'toDate'> | null | undefined
): E[] => events.filter((event) => {
  if (!isFacilityCost(event as never)) return false
  if (scope?.farmIds?.length && !scope.farmIds.includes(event.farmId)) return false
  const day = String(event.recordedAt ?? '').slice(0, 10)
  if (scope?.fromDate && day < scope.fromDate) return false
  if (scope?.toDate && day > scope.toDate) return false
  return true
})

export default function FacilityCostsSummary({ scope }: { scope: ReportScopeV1 | null | undefined }) {
  const t = useT()
  const [totals, setTotals] = useState<Array<[string, number]> | null>(null)
  const scopeKey = JSON.stringify([scope?.farmIds ?? [], scope?.fromDate ?? '', scope?.toDate ?? ''])

  useEffect(() => {
    let cancelled = false
    void dexieLotCostEventRepo.list().then((events) => {
      if (!cancelled) setTotals(sumCostEventsByCurrency(facilityCostsInScope(events, scope)))
    }).catch(() => {
      if (!cancelled) setTotals(null)
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey])

  if (!totals || totals.length === 0) return null
  return (
    <div data-testid="report-facility-costs" className="lead" style={{ marginTop: 8 }}>
      <strong>{t('costs.facilityWide.totalLabel')}:</strong>{' '}
      {totals.map(([currency, cents]) => formatCostEventDisplayAmount(cents, currency)).join(' · ')}
      {' — '}{t('reports.facilityCosts.note')}
    </div>
  )
}
